import React, { useState, useEffect, useRef } from 'react';
import axios from "axios";
import { useParams } from 'react-router-dom';
import { API } from "../utils/net-api";
import Markdown from 'react-markdown';
import blackLogo from "../images/black_logo.png";
import timeIcon from "../images/time.png";
import grayTimeIcon from "../images/gray_time.png";
import defaultAvatar from "../images/avatar_default.png"; // 导入iOS默认头像
import greenPoint from "../images/green_point.png"; // 导入绿色标记点
import arrayEmpty from "../images/array_empty.png"; // 导入无评论状态图片
import arrayError from "../images/array_error.png"; // 导入评论加载错误状态图片
import arrayWarn from "../images/array_warn.png"; // 导入评论加载错误状态图片
import heartSingle18 from "../images/heart_single_18.png"; // 导入未点赞心形图片
import heartSingleRed18 from "../images/heart_single_red_18.png"; // 导入已点赞心形图片
import { Helmet } from 'react-helmet';
import './wave-detail-page.css'; // 新的样式表
import { MobileStepper } from '@mui/material';
import { lineHeight } from '@mui/system';

// HostTag 组件 (模仿iOS样式)
const HostTag = () => {
    return (
        <span style={{
            fontSize: '12px',
            height: '19px',
            padding: '0 8px',
            backgroundColor: 'rgba(159, 33, 237, 0.05)', // #9F21ED opacity 0.05
            color: '#9F21ED',
            borderRadius: '9.5px', // half of height for capsule shape
            display: 'inline-flex',
            alignItems: 'center',
            marginLeft: '8px'
        }}>
            Host
        </span>
    );
};

// 错误显示组件
const ErrorDisplay = () => {
    return (
        <div className="error-display-container">
            <div className="error-display-content">
                <div className="error-image-container">
                    <img src={arrayWarn} alt="Error" className="error-image" />
                </div>
                <div className="error-text-container">
                    <h2 className="error-title">Ops!</h2>
                    <p className="error-message">Something went wrong!</p>
                </div>
            </div>
        </div>
    );
};

// 自定义组件来替换Markdown中的链接
const CustomMarkdown = ({ children }) => {
    return (
        <Markdown
            components={{
                // 自定义链接渲染
                a: ({ node, ...props }) => (
                    <div style={{ color: "#4B87FF", lineHeight: "22px", fontSize: "14px" }}>{props.children}</div>
                )
            }}
        >
            {children}
        </Markdown>
    );
};

// 格式化相对时间
const formatRelativeTime = (isoDateString) => {
    const date = new Date(isoDateString);
    const now = new Date();
    const diffSeconds = Math.floor((now.getTime() - date.getTime()) / 1000);

    if (diffSeconds < 60) {
        return "now";
    } else if (diffSeconds < 3600) {
        return `${Math.floor(diffSeconds / 60)} mins ago`;
    } else if (diffSeconds < 86400) {
        return `${Math.floor(diffSeconds / 3600)} hours ago`;
    } else if (diffSeconds < 2592000) { // 30 days
        return `${Math.floor(diffSeconds / 86400)} days ago`;
    } else {
        // 如果超过30天，显示标准日期格式
        return date.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
    }
};

export function WaveDetailPage() {
    const { id } = useParams();
    const [value, setValue] = useState();
    const [isLoading, setIsLoading] = useState(true);
    const [isError, setIsError] = useState(false); // 添加错误状态
    const [currentImageIndex, setCurrentImageIndex] = useState(0);
    const [touchStart, setTouchStart] = useState(0);
    const [touchEnd, setTouchEnd] = useState(0);
    const [isDragging, setIsDragging] = useState(false);
    const [dragStartX, setDragStartX] = useState(0);
    const [dragOffset, setDragOffset] = useState(0);
    const [isClick, setIsClick] = useState(false);
    // 移除 comments 和 commentLoadState 状态
    // const [comments, setComments] = useState([]); 
    // const [commentLoadState, setCommentLoadState] = useState('loading'); 

    // 轮播图引用
    const carouselRef = useRef(null);
    const trackRef = useRef(null);

    useEffect(() => {
        document.title = "Marlo - Connect, Meet, Nearby";
        loadData();
        // fetchComments(); // 移除对 fetchComments 的调用
    }, []);

    // 当currentImageIndex变化时，更新轮播位置
    useEffect(() => {
        if (trackRef.current && !isDragging) {
            trackRef.current.style.transition = 'transform 0.3s ease-out';
            trackRef.current.style.transform = `translateX(-${currentImageIndex * 100}%)`;
        }
    }, [currentImageIndex]);

    // 获取地图URL
    const getMapUrl = (waveId) => {
        return API.mapSnapshot.replace('WAVE_ID', waveId);
    };

    const loadData = () => {
        setIsLoading(true);
        setIsError(false); // 重置错误状态
        axios.get(`${API.htmlDetail}/${id}`)
            .then((res) => {
                if (res.status !== 200) {
                    console.error(`Get wave error: status ${res.status}`);
                    setValue({});
                    setIsError(true); // 设置错误状态
                    setIsLoading(false);
                    return;
                }
                if (!res.data.Succeed) {
                    console.error(`Get wave error: ${res.data.Message}`);
                    setValue({});
                    setIsError(true); // 设置错误状态
                    setIsLoading(false);
                    return;
                }
                const data = res.data.Data;
                document.title = data.Title || "Marlo - Wave Detail";
                setValue(data);
                setIsLoading(false);
                console.log("API返回数据:", data);
            })
            .catch((error) => {
                console.error(`Get wave error: ${error}`);
                setValue({});
                setIsError(true); // 设置错误状态
                setIsLoading(false);
            });
    };

    // 移除 fetchComments 函数
    // const fetchComments = () => {
    //     setCommentLoadState('loading');
    //     axios.post(`${API.base}/api/v1/wave/list/${id}/comments`, {
    //         offset: 0,
    //         limit: 50 
    //     })
    //     .then((res) => {
    //         if (res.status !== 200) {
    //             setCommentLoadState('error');
    //             console.error(`Get comments error: ${res.status}`);
    //             return;
    //         }
    //         if (!res.data.Succeed) {
    //             setCommentLoadState('error');
    //             console.error(`Get comments error: ${res.data.Message}`);
    //             return;
    //         }
    //         const data = res.data.Data;
    //         setComments(data);
    //         if (data.length === 0) {
    //             setCommentLoadState('succeed_empty'); 
    //         } else {
    //             setCommentLoadState('succeed');
    //         }
    //         console.log("API返回评论数据:", data);
    //     })
    //     .catch((error) => {
    //         setCommentLoadState('error');
    //         console.error(`Get comments error: ${error}`);
    //     });
    // };

    // 计算剩余天数
    const calculateDaysLeft = (expiredAt, isFake) => {
        if (isFake) {
            return { display: "7 days left", isExpired: false };
        }
        if (!expiredAt) return { display: "7 days left", isExpired: false };
        const expired = new Date(expiredAt);
        const now = new Date();
        const diffTime = expired - now;
        const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));
        const isExpired = diffDays <= 0;
        return { display: diffDays > 0 ? `${diffDays} days left` : "Expired", isExpired };
    };

    // 获取参与者数量显示格式
    const getCapacityString = () => {
        if (!value) return "0/0";

        // 参与者数量计算：explore页面不包含创建者，其他页面包含创建者
        const behaviorCount = value.Behaviors ? value.Behaviors.length : 0;
        const attendeeCount = value.IsExplore ? behaviorCount : behaviorCount + 1;

        // 容量显示，如果超过2147483647则显示为"Unlimited"
        const capacityString = value.Capacity >= 2147483647 ? "Unlimited" : value.Capacity;

        return `${attendeeCount}/${capacityString}`;
    };

    // 生成参与者头像列表
    const renderAvatars = () => {
        if (!value) return null;

        // 获取实际参与者数量
        const totalAttendees = value.Behaviors ? value.Behaviors.length : 0;
        const displayLimit = 4; // 最多显示4个头像

        // 如果是Explore页面且参与者为0，显示默认头像
        if (value.IsExplore && totalAttendees === 0) {
            return (
                <div className="avatar-small">
                    <img
                        src="/images/avatar_no.png" // 需要添加这个默认头像图片
                        alt="No participants"
                        className="avatar-placeholder-img"
                    />
                </div>
            );
        }

        if (!value.Behaviors) return null;

        // 创建头像数组
        const avatars = [];

        // 添加参与者头像
        for (let i = 0; i < Math.min(totalAttendees, displayLimit); i++) {
            const behavior = value.Behaviors[i];
            const profilePicture = behavior.User && behavior.User.ProfilePicture
                ? behavior.User.ProfilePicture
                : defaultAvatar;

            avatars.push(
                <div key={i} className="avatar-small">
                    <img
                        src={profilePicture}
                        alt={`Participant ${i + 1}`}
                        className="avatar-placeholder-img"
                    />
                </div>
            );
        }

        // 如果参与者数量超过显示限制，添加"更多"指示器
        if (totalAttendees > displayLimit) {
            avatars.push(
                <div key="more" className="avatar-more">
                    <span className="avatar-more-text">{totalAttendees - displayLimit}+</span>
                </div>
            );
        }

        return avatars;
    };

    // 处理触摸事件
    const handleTouchStart = (e) => {
        setTouchStart(e.targetTouches[0].clientX);
        setIsDragging(true);
        setDragStartX(e.targetTouches[0].clientX);
        setDragOffset(0);
        setIsClick(true); // 初始假设这是一个点击

        if (trackRef.current) {
            trackRef.current.style.transition = 'none';
        }
    };

    const handleTouchMove = (e) => {
        if (!isDragging || !value?.Media || value.Media.length <= 1) return;

        const currentX = e.targetTouches[0].clientX;
        setTouchEnd(currentX);

        const newOffset = currentX - dragStartX;

        // 如果移动距离超过5像素，认为是拖动而不是点击
        if (Math.abs(newOffset) > 5) {
            setIsClick(false);
        }

        setDragOffset(newOffset);

        if (trackRef.current) {
            const translateX = -currentImageIndex * 100 + (newOffset / trackRef.current.clientWidth * 100);
            trackRef.current.style.transform = `translateX(${translateX}%)`;
        }
    };

    const handleTouchEnd = (e) => {
        if (!isDragging || !value?.Media || value.Media.length <= 1) return;

        setIsDragging(false);

        // 如果是点击事件，不做任何处理
        if (isClick) {
            return;
        }

        if (trackRef.current) {
            trackRef.current.style.transition = 'transform 0.3s ease-out';
        }

        const minSwipeDistance = 50;
        if (touchStart - touchEnd > minSwipeDistance) {
            // 向左滑动，显示下一张
            setCurrentImageIndex((prevIndex) => (prevIndex + 1) % value.Media.length);
        } else if (touchEnd - touchStart > minSwipeDistance) {
            // 向右滑动，显示上一张
            setCurrentImageIndex((prevIndex) => (prevIndex - 1 + value.Media.length) % value.Media.length);
        } else {
            // 回到原始位置
            if (trackRef.current) {
                trackRef.current.style.transform = `translateX(-${currentImageIndex * 100}%)`;
            }
        }
    };

    // 处理鼠标事件（PC端）
    const handleMouseDown = (e) => {
        // 只处理左键点击的拖动
        if (e.button !== 0) return;

        e.preventDefault(); // 阻止默认行为
        setIsDragging(true);
        setDragStartX(e.clientX);
        setDragOffset(0);
        setIsClick(true); // 初始假设这是一个点击

        if (trackRef.current) {
            trackRef.current.style.transition = 'none';
        }
    };

    const handleMouseMove = (e) => {
        if (!isDragging || !value?.Media || value.Media.length <= 1) return;

        const newOffset = e.clientX - dragStartX;

        // 如果移动距离超过5像素，认为是拖动而不是点击
        if (Math.abs(newOffset) > 5) {
            setIsClick(false);
        }

        setDragOffset(newOffset);

        if (trackRef.current) {
            const translateX = -currentImageIndex * 100 + (newOffset / trackRef.current.clientWidth * 100);
            trackRef.current.style.transform = `translateX(${translateX}%)`;
        }
    };

    const handleMouseUp = (e) => {
        if (!isDragging || !value?.Media || value.Media.length <= 1) return;

        setIsDragging(false);

        // 如果是点击事件，不做任何处理
        if (isClick) {
            return;
        }

        if (trackRef.current) {
            trackRef.current.style.transition = 'transform 0.3s ease-out';
        }

        const minSwipeDistance = 50;
        if (Math.abs(dragOffset) > minSwipeDistance) {
            if (dragOffset < 0) {
                // 向左滑动，显示下一张
                setCurrentImageIndex((prevIndex) => (prevIndex + 1) % value.Media.length);
            } else {
                // 向右滑动，显示上一张
                setCurrentImageIndex((prevIndex) => (prevIndex - 1 + value.Media.length) % value.Media.length);
            }
        } else {
            // 回到原始位置
            if (trackRef.current) {
                trackRef.current.style.transform = `translateX(-${currentImageIndex * 100}%)`;
            }
        }
    };

    const handleMouseLeave = () => {
        if (isDragging) {
            setIsDragging(false);

            // 回到原始位置
            if (trackRef.current) {
                trackRef.current.style.transition = 'transform 0.3s ease-out';
                trackRef.current.style.transform = `translateX(-${currentImageIndex * 100}%)`;
            }
        }
    };

    // 阻止点击事件传播
    const handleClick = (e) => {
        e.stopPropagation();
        e.preventDefault();
    };

    if (isLoading) {
        return (
            <div className="wave-loading">
                <div className="loading-spinner"></div>
                <p>Loading...</p>
            </div>
        );
    }

    if (!value) return null;

    const appStoreUrl = 'https://apps.apple.com/us/app/marlo-connect-meet-nearby/id6450737280';
    const deepLinkUrl = `marlo://wave/${id}`;

    // 检查是否有图片媒体
    const hasMedia = value.Media && value.Media.length > 0;

    // 检查User是否存在，如果不存在则使用默认值
    const hostName = value.User ? value.User.FirstName : "Unknown Host";
    const hostImage = value.User && value.User.ProfilePicture ? value.User.ProfilePicture : null;

    // 获取地图URL
    const mapImageUrl = getMapUrl(id);

    // 判断wave是否过期
    const isWaveExpired = calculateDaysLeft(value.ExpiredAt, value.IsFake).isExpired;

    // 处理应用内跳转逻辑
    const handleOpenInApp = () => {
        // 检测环境
        const isWeChat = /micromessenger/i.test(navigator.userAgent);
        const isSafari = /safari/i.test(navigator.userAgent) && !/chrome/i.test(navigator.userAgent);
        
        if (isWaveExpired) {
            // 过期的Wave跳转到app首页
            if (isWeChat) {
                // 微信中使用Universal Link到首页
                window.location.href = 'https://mobile.himarlo.com/profile/';
            } else {
                // 其他环境使用URL Scheme
                window.location.href = 'marlo://';
            }
            return;
        }

        // 未过期的Wave跳转到具体详情页
        const universalLink = `https://mobile.himarlo.com/wave/${id}`;
        const urlScheme = `marlo://wave/${id}`;
        
        if (isWeChat) {
            // 微信中优先使用Universal Link
            window.location.href = universalLink;
        } else if (isSafari) {
            // Safari中使用特殊处理：先尝试URL Scheme，避免页面跳转
            const iframe = document.createElement('iframe');
            iframe.style.display = 'none';
            iframe.src = urlScheme;
            document.body.appendChild(iframe);
            
            // 如果URL Scheme失败，300ms后使用Universal Link
            setTimeout(() => {
                document.body.removeChild(iframe);
                if (!document.hidden) {
                    // 在新窗口打开Universal Link，避免当前页面被替换
                    window.open(universalLink, '_blank');
                }
            }, 300);
        } else {
            // 其他环境先尝试URL Scheme
            window.location.href = urlScheme;
            
            // 备选：500ms后使用Universal Link
            setTimeout(() => {
                if (!document.hidden) {
                    window.location.href = universalLink;
                }
            }, 500);
        }
    };

    // 渲染评论部分
    const renderCommentsSection = () => {
        // 自定义比较函数，用于根据Path字段进行排序
        const comparePaths = (pathA, pathB) => {
            const partsA = pathA.split('.').map(Number);
            const partsB = pathB.split('.').map(Number);

            for (let i = 0; i < Math.min(partsA.length, partsB.length); i++) {
                if (partsA[i] !== partsB[i]) {
                    return partsA[i] - partsB[i];
                }
            }
            return partsA.length - partsB.length; // 较短的路径（即更上层的评论）排在前面
        };

        // 直接从 value.Comments 中获取数据并进行排序
        const commentsData = (value?.Comments || []).sort((a, b) => {
            if (!a.Path && !b.Path) return 0;
            if (!a.Path) return 1; // Path为空的排在后面
            if (!b.Path) return -1; // Path为空的排在后面
            return comparePaths(a.Path, b.Path);
        });
        const commentLoadState = isLoading ? 'loading' : (commentsData.length === 0 ? 'succeed_empty' : 'succeed');

        if (commentLoadState === 'loading') {
            return (
                <div className="comments-loading">
                    <div className="loading-spinner"></div>
                    <p>Loading comments...</p>
                </div>
            );
        } else if (commentLoadState === 'succeed_empty') {
            return (
                <div className="comments-empty">
                    <img src={arrayEmpty} alt="No Comments" className="empty-image" />
                    <p className="empty-text">No comments yet</p>
                </div>
            );
        } else if (commentLoadState === 'error') {
            return (
                <div className="comments-error">
                    <img src={arrayError} alt="Error" className="error-image" />
                    <p className="error-text">Oops, something happens wrong</p>
                </div>
            );
        } else if (commentLoadState === 'succeed') {
            return (
                <div className="comments-list">
                    {commentsData.map((comment, index) => {
                        // 判断是否为回复评论 (Path字段包含多个部分即为回复)
                        const isReply = comment.Path && comment.Path.split('.').length > 1;

                        // 判断是否需要显示横线：只有当评论是顶层评论（非回复），且不是第一个评论时才显示
                        const showDivider = !isReply && index > 0;

                        return (
                            <React.Fragment key={comment.ID || index}>
                                {showDivider && <div className="comment-divider"></div>}
                                <div className={`comment-item ${isReply ? 'comment-reply' : ''}`}>
                                    <div className="comment-header">
                                        <img
                                            src={comment.User && comment.User.ProfilePicture ? comment.User.ProfilePicture : defaultAvatar}
                                            alt="User Avatar"
                                            className="comment-avatar"
                                        />
                                        <div className="comment-info">
                                            <span className="comment-username">{comment.User ? comment.User.FirstName : 'Unknown'}
                                                {value.User && comment.User && value.User.ID === comment.User.ID && (
                                                    <HostTag />
                                                )}
                                            </span>
                                            <span className="comment-time">{formatRelativeTime(comment.CreatedAt)}</span>
                                        </div>
                                        <div className="comment-actions">
                                            <img
                                                src={comment.Feedbacks && comment.Feedbacks.some(f => f.UserID === value.User.ID && f.IsFollow) ? heartSingleRed18 : heartSingle18}
                                                alt="Like Button"
                                                className="comment-like-icon"
                                                onClick={handleOpenInApp}
                                            />
                                            <span className="comment-like-count">{comment.Feedbacks ? comment.Feedbacks.filter(f => f.IsFollow).length : 0}</span>
                                        </div>
                                    </div>
                                    <p className="comment-content">{comment.Content}</p>
                                </div>
                            </React.Fragment>
                        );
                    })}
                </div>
            );
        }
        return null;
    };

    return (
        <div className="wave-container">
            <Helmet>
                <meta name="viewport" content="width=device-width, initial-scale=1.0, maximum-scale=1.0, user-scalable=no" />
                <link rel="preconnect" href="https://fonts.googleapis.com" />
                <link rel="preconnect" href="https://fonts.gstatic.com" crossOrigin="anonymous" />
                <link href="https://fonts.googleapis.com/css2?family=Lexend:wght@300;400;500;600;700&display=swap" rel="stylesheet" />
            </Helmet>

            {/* 顶部导航栏 */}
            <div className="app-header">
                <div className="logo-container">
                    <img src={blackLogo} alt="Marlo Logo" className="logo-image" />
                    <div className="logo">MARLO</div>
                </div>
                <a href={appStoreUrl} className="download-btn">Download App</a>
            </div>

            {/* 如果有错误，显示错误提示模块 */}
            {isError ? (
                <ErrorDisplay />
            ) : (
                <>
                    {/* Wave 头部区域 */}
                    {hasMedia ? (<div
                        className="wave-header"
                        ref={carouselRef}
                    >

                        <div className="carousel-container"
                            onTouchStart={handleTouchStart}
                            onTouchMove={handleTouchMove}
                            onTouchEnd={handleTouchEnd}
                            onMouseDown={handleMouseDown}
                            onMouseMove={handleMouseMove}
                            onMouseUp={handleMouseUp}
                            onMouseLeave={handleMouseLeave}
                            onClick={handleClick}
                        >
                            <div className="carousel-track" ref={trackRef}>
                                {value.Media.map((media, index) => (
                                    <div key={index} className="carousel-slide">
                                        <img src={media.FilePath} alt={`Wave image ${index + 1}`} className="wave-media" />
                                    </div>
                                ))}
                            </div>
                            {value.Media.length > 1 && (
                                <div className="ios-style-indicators" onClick={handleClick}>
                                    <MobileStepper
                                        variant="dots"
                                        steps={value.Media.length}
                                        position="static"
                                        activeStep={currentImageIndex}
                                        className="ios-style-stepper"
                                        nextButton={null}
                                        backButton={null}
                                    />
                                </div>
                            )}
                        </div>
                        <div className="wave-tags">
                            <div className="wave-tag time-tag">
                                <div className="icon-container">
                                    <img src={timeIcon} alt="Time" className="time-icon" />
                                </div>
                                <span className="time-text">
                                    {value.IsExplore 
                                        ? `${value.EventTimeStr} ${value.EventDateStr}, ${value.EventDayOfWeek}`
                                        : calculateDaysLeft(value.ExpiredAt, value.IsFake).display
                                    }
                                </span>
                            </div>
                        </div>
                    </div>) : (<div className="wave-no-media">
                <div className="wave-tag-no-media time-tag">
                <div className="icon-container">
                    <img src={grayTimeIcon} alt="Time" className="time-icon" />
                </div>
                <span className="time-text">
                    {value.IsExplore 
                        ? `${value.EventTimeStr} ${value.EventDateStr}, ${value.EventDayOfWeek}`
                        : calculateDaysLeft(value.ExpiredAt, value.IsFake).display
                    }
                </span>
            </div>

            </div>)}


            {/* Wave 内容区域 */}
            <div className="wave-content">
                {/* 标题 */}
                <h1 className="wave-title">{value.Title}</h1>

                {/* 正文内容 */}
                <div className="wave-body" onClick={handleOpenInApp}>
                    <CustomMarkdown>{value.Body}</CustomMarkdown>
                </div>

                <div className="divider"></div>

                {/* 主办方信息 - 只在非explore页面显示 */}
                {!value.IsExplore && (
                    <>
                        <div className="wave-host-section">
                            <div className="section-label">Hosted By</div>
                            <div className="host-info">
                                <div className="avatar" onClick={handleOpenInApp}>
                                    {hostImage ?
                                        <img src={hostImage} alt="Host" /> :
                                        <img src={defaultAvatar} alt="Default Avatar" className="avatar-placeholder-img" />
                                    }
                                </div>
                                <div className="host-name">{hostName}</div>
                            </div>
                        </div>
                        <div className="divider"></div>
                    </>
                )}

                {/* 参与者信息 - 只在非Explore页面显示 */}
                {!value.IsExplore && (
                    <>
                        <div className="wave-capacity-section">
                            <div className="section-label">Participants:</div>
                            <span className="capacity-value">
                                {getCapacityString()}
                            </span>
                            <div className="avatars-container">
                                {renderAvatars()}
                            </div>
                        </div>

                        <div className="divider"></div>
                    </>
                )}

                {/* Explore页面的位置信息 */}
                {value.IsExplore && (
                    <div className="explore-location-section">
                        <div className="location-row">
                            <span className="section-label">Location:</span>
                            <div className="location-content">
                                <img src="/images/blue_addr.png" alt="Location" className="location-icon" />
                                <span className="location-text-blue">{value.DetailedLocation}</span>
                            </div>
                        </div>
                    </div>
                )}

                <div className="divider"></div>

                {/* 地点信息 */}
                {value.Coverages && value.Coverages.length > 0 && (
                    <div className="wave-location-section" onClick={handleOpenInApp}>
                        <img
                            src={mapImageUrl}
                            alt="Location Map"
                            className="location-map-image"
                            onError={(e) => {
                                console.error("地图加载失败");
                                e.target.style.display = 'none';
                            }}
                        />
                        <img
                            src={greenPoint}
                            alt="Location Marker"
                            className="map-marker-point"
                        />
                    </div>
                )}

                {/* 评论部分 - 只在非Explore页面显示 */}
                {!value.IsExplore && (
                    <>
                        {/* 评论计数 */}
                        <div className="comments-header">
                            <h2>{value.Comments ? value.Comments.length : 0} Comments</h2>
                        </div>

                        {/* 评论部分渲染 */}
                        {renderCommentsSection()}
                    </>
                )}

            </div>
        </>
            )}
            
            {/* 底部按钮 - 始终显示 */}
            <div className="app-cta">
                <a onClick={handleOpenInApp} className="cta-button">View in Marlo App</a>
            </div>
        </div>
    );
}